import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, Calendar, Clock, User, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfWeek, addDays, isSameMonth, isSameDay, getDay } from "date-fns";
import { de, enUS } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXP_COLORS = [
  { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500", border: "border-emerald-300" },
  { bg: "bg-sky-100", text: "text-sky-800", dot: "bg-sky-500", border: "border-sky-300" },
  { bg: "bg-amber-100", text: "text-amber-800", dot: "bg-amber-500", border: "border-amber-300" },
  { bg: "bg-purple-100", text: "text-purple-800", dot: "bg-purple-500", border: "border-purple-300" },
  { bg: "bg-rose-100", text: "text-rose-800", dot: "bg-rose-500", border: "border-rose-300" },
  { bg: "bg-teal-100", text: "text-teal-800", dot: "bg-teal-500", border: "border-teal-300" },
];

export default function SuggestionTab({ language, onSlotCreated }) {
  const dateLocale = language === "de" ? de : enUS;
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [plans, setPlans] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [experimenters, setExperimenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(0);
  const [addingSlot, setAddingSlot] = useState(null);

  const monthNames = language === "de"
    ? ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"]
    : ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const dayHeaders = language === "de" ? ["Mo","Di","Mi","Do","Fr","Sa","So"] : ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  const getExpColor = (expId) => {
    const idx = experimenters.findIndex(e => e.id === expId);
    return EXP_COLORS[idx % EXP_COLORS.length];
  };

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/monthly-plans`, { year, month }, { withCredentials: true });
      setPlans(res.data.plans || []);
      setAvailability(res.data.availability || []);
      setExperimenters(res.data.experimenters || []);
      setSelectedPlan(0);
      if (!res.data.plans?.length) toast.info(language === "de" ? "Keine Pläne möglich" : "No plans possible");
    } catch (e) {
      toast.error("Error");
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const handleAddToAppointments = async (assignment) => {
    const key = assignment.date + assignment.time;
    setAddingSlot(key);
    try {
      // Create as time slot with 7-day second session
      const s1Date = new Date(assignment.date);
      const s2Date = new Date(s1Date);
      s2Date.setDate(s2Date.getDate() + 7);
      const s2DateStr = s2Date.toISOString().split("T")[0];
      
      await axios.post(`${API}/admin/time-slots`, {
        date: assignment.date,
        time: assignment.time,
        session2_date: s2DateStr,
        session2_time: assignment.time,
        experimenter1_id: assignment.experimenter_id,
        experimenter2_id: assignment.experimenter_id
      }, { withCredentials: true });
      toast.success(`${assignment.experimenter} ${assignment.time}–${assignment.end_time} ${language === "de" ? "hinzugefügt!" : "added!"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setAddingSlot(null);
    }
  };

  // Build calendar grid
  const buildCalendarWeeks = () => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = endOfMonth(firstDay);
    const start = startOfWeek(firstDay, { weekStartsOn: 1 });
    const weeks = [];
    let current = start;
    while (current <= lastDay || weeks.length < 5) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(current));
        current = addDays(current, 1);
      }
      weeks.push(week);
      if (current > addDays(lastDay, 7)) break;
    }
    return weeks;
  };

  const calendarWeeks = buildCalendarWeeks();
  const currentPlan = plans[selectedPlan];

  const getAssignmentsForDay = (day) => {
    if (!currentPlan) return [];
    const dateStr = format(day, "yyyy-MM-dd");
    return currentPlan.assignments.filter(a => a.date === dateStr);
  };

  const getAvailForDay = (day) => {
    const dateStr = format(day, "yyyy-MM-dd");
    return availability.find(a => a.date === dateStr);
  };

  return (
    <div className="space-y-6">
      {/* Month selector + Generate */}
      <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="font-outfit text-lg text-forest-800 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                {language === "de" ? "Monatliche Terminplanung" : "Monthly Appointment Planning"}
              </CardTitle>
              <CardDescription className="text-sm mt-1">
                {language === "de"
                  ? "Kombinationen basierend auf Verfügbarkeit und Kapazität aller Experimenter"
                  : "Combinations based on all experimenters' availability and capacity"}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={prevMonth} className="h-8 w-8 p-0 rounded-lg"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="font-medium text-forest-900 min-w-[140px] text-center">{monthNames[month - 1]} {year}</span>
              <Button variant="outline" size="sm" onClick={nextMonth} className="h-8 w-8 p-0 rounded-lg"><ChevronRight className="h-4 w-4" /></Button>
              <Button onClick={fetchPlans} disabled={loading} className="bg-forest-800 hover:bg-forest-900 text-white rounded-full ml-2" data-testid="generate-plans-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {language === "de" ? "Pläne generieren" : "Generate Plans"}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Plan selector + Summary */}
      {plans.length > 0 && (
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Plan tabs */}
          <div className="flex lg:flex-col gap-2">
            {plans.map((p, idx) => (
              <button key={idx} onClick={() => setSelectedPlan(idx)}
                className={`px-4 py-3 rounded-xl text-left transition-all border-2 min-w-[160px] ${selectedPlan === idx ? "bg-forest-800 text-white border-forest-800" : "bg-white text-forest-900 border-stone-200 hover:border-forest-300"}`}
                data-testid={`plan-tab-${idx}`}
              >
                <p className="font-medium text-sm">{language === "de" ? "Kombination" : "Combination"} {idx + 1}</p>
                <p className={`text-xs mt-0.5 ${selectedPlan === idx ? "text-forest-100" : "text-stone-500"}`}>
                  {p.assignments.length} {language === "de" ? "Sitzungen" : "sessions"}
                </p>
              </button>
            ))}
          </div>

          {/* Summary cards */}
          {currentPlan && (
            <div className="flex-1">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
                {currentPlan.summary.map((s, idx) => {
                  const color = EXP_COLORS[idx % EXP_COLORS.length];
                  return (
                    <div key={s.id} className={`p-3 rounded-xl border ${color.border} ${color.bg}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className={`h-2.5 w-2.5 rounded-full ${color.dot}`} />
                        <span className={`text-xs font-semibold ${color.text}`}>{s.name}</span>
                      </div>
                      <p className={`text-lg font-bold ${color.text}`}>{s.assigned_hours}h</p>
                      <p className="text-[10px] text-stone-500">{s.assigned_sessions}/{s.target_sessions} Sitz. | {s.fill_pct}%</p>
                      <div className="w-full bg-white/50 rounded-full h-1.5 mt-1">
                        <div className={`${color.dot} h-1.5 rounded-full`} style={{ width: `${Math.min(s.fill_pct, 100)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Monthly Calendar */}
              <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {dayHeaders.map(d => (
                      <div key={d} className="text-center text-xs font-medium text-stone-500 py-1">{d}</div>
                    ))}
                  </div>
                  {calendarWeeks.map((week, wIdx) => (
                    <div key={wIdx} className="grid grid-cols-7 gap-1 mb-1">
                      {week.map((day, dIdx) => {
                        const inMonth = isSameMonth(day, new Date(year, month - 1));
                        const isToday = isSameDay(day, new Date());
                        const assignments = getAssignmentsForDay(day);
                        const avail = getAvailForDay(day);
                        return (
                          <div key={dIdx} className={`min-h-[80px] rounded-lg p-1 border ${!inMonth ? "bg-stone-50/30 border-transparent" : isToday ? "border-forest-400 bg-forest-50/30" : "border-stone-100"}`}>
                            <p className={`text-xs font-medium mb-0.5 ${!inMonth ? "text-stone-300" : isToday ? "text-forest-800 font-bold" : "text-stone-500"}`}>
                              {format(day, "d")}
                            </p>
                            {inMonth && assignments.map((a, aIdx) => {
                              const c = getExpColor(a.experimenter_id);
                              const isAdding = addingSlot === a.date + a.time;
                              return (
                                <button key={aIdx} onClick={() => handleAddToAppointments(a)}
                                  disabled={isAdding}
                                  className={`w-full rounded px-1 py-0.5 mb-0.5 ${c.bg} ${c.text} text-left hover:ring-2 hover:ring-forest-400 transition-all cursor-pointer`}
                                  title={language === "de" ? "Klicken zum Hinzufügen" : "Click to add"}>
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-[10px] font-semibold leading-tight">{a.time}–{a.end_time || ""}</p>
                                      <p className="text-[9px] leading-tight truncate">{a.experimenter}</p>
                                    </div>
                                    {isAdding
                                      ? <Loader2 className="h-2.5 w-2.5 animate-spin flex-shrink-0" />
                                      : <Plus className="h-2.5 w-2.5 opacity-50 flex-shrink-0" />}
                                  </div>
                                </button>
                              );
                            })}
                            {inMonth && assignments.length === 0 && avail && avail.experimenters.length > 0 && (
                              <div className="flex flex-wrap gap-0.5 mt-0.5">
                                {avail.experimenters.map(e => (
                                  <div key={e.id} className={`h-1.5 w-1.5 rounded-full ${getExpColor(e.id).dot} opacity-30`} title={`${e.name}: ${e.times.join(", ")}`} />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Legend */}
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                {experimenters.map((e, idx) => (
                  <div key={e.id} className="flex items-center gap-1.5">
                    <div className={`h-3 w-3 rounded-full ${EXP_COLORS[idx % EXP_COLORS.length].dot}`} />
                    <span className="text-xs text-stone-600">{e.name}</span>
                  </div>
                ))}
                <div className="flex items-center gap-1.5 ml-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-stone-400 opacity-30" />
                  <span className="text-xs text-stone-400">{language === "de" ? "Verfügbar (unbesetzt)" : "Available (unassigned)"}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {plans.length === 0 && !loading && (
        <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
          <CardContent className="text-center py-12">
            <Calendar className="h-12 w-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 text-sm">
              {language === "de"
                ? "Wählen Sie einen Monat und klicken Sie auf 'Pläne generieren'"
                : "Select a month and click 'Generate Plans'"}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
